import { ApiClient } from "@anna-money/anna-web-lib/api";
import { type NavigateFunction } from "react-router-dom";
import { Memoize } from "typescript-memoize";
import type { ABTestingService } from "services/abTesting/abTestingService";
import { AttachmentClient } from "services/attachments/attachmentClient";
import { AttachmentStore } from "services/attachments/attachmentStore";
import { ExpressCheckoutStore } from "services/expressCheckout/expressCheckoutStore";
import { type PartnerOffersStore } from "services/partnerOffers/partnerOffersStore";
import { BasketQuestionsStore } from "services/questions/gb/basketQuestionsStore";
import { CHVerificationQuestionStoreV2 } from "services/questions/gb/chVerificationQuestionStoreV2";
import { InitQuestionStore } from "services/questions/gb/initQuestionStore";
import { ParticularQuestionStore } from "services/questions/gb/particularsQuestionsStore";
import { RejectionAddressStore } from "services/rejectionAddressStore/rejectionAddressStore";
import { CHVerificationStepStore } from "services/stepStore/gb/chVerificationStepStore";
import { IdentityStepStore } from "services/stepStore/gb/identityStepStore";
import { InitStepStore } from "services/stepStore/gb/initStepStore";
import { PricingStepStore } from "services/stepStore/gb/pricingStepStore";
import { AuthenticationHandler } from "./auth/authenticationApiHandler";
import { BaseServices, Services } from "./services";
import { BillingClient } from "./services/billing/billingClient";
import { BillingItemsStore } from "./services/billingItems/gb/billingItemsStore";
import { BusinessActivitiesSelectStore } from "./services/businessActivitiesSelect/gb/businessActivitiesSelectStore";
import { CheckoutBasketStore } from "./services/checkoutBasket/gb/checkoutBasketStore";
import { CompanyAddressStore } from "./services/companyAddress/gb/companyAddressStore";
import { CompanyAddressFormStore } from "./services/companyAddressForm/gb/companyAddressFormStore";
import { CompanyBusinessActivitiesClient } from "./services/companyBusinessActivities/gb/companyBusinessActivitiesClient";
import { CompanyBusinessActivitiesStore } from "./services/companyBusinessActivities/gb/companyBusinessActivitiesStore";
import { CompanyNameFormStore } from "./services/companyNameForm/gb/companyNameFormStore";
import { DirectorCodeModalStore } from "./services/directorCodeModal/directorCodeModalStore";
import { DirectorFormStore } from "./services/directorForm/directorFormStore";
import { DirectorPoaWidgetStore } from "./services/directorIdentity/directorPoaWidgetStore";
import { FeatureStore } from "./services/feature/featureStore";
import { IncorporationRegisterFormStepsStore } from "./services/incorporationRegisterForm/incorporationRegisterFormStepsStore";
import { IncorporationRegisterFormStore } from "./services/incorporationRegisterForm/incorporationRegisterFormStore";
import { PricingPlanStore } from "./services/pricingPlan/gb/pricingPlanStore";
import { PricingPlanClient } from "./services/pricingPlan/pricingPlanClient";
import { PricingPlanFormStore } from "./services/pricingPlanForm/gb/pricingPlanFormStore";
import { ProfileStore } from "./services/profile/profileStore";
import { CompanyQuestionsStore } from "./services/questions/gb/companyQuestionsStore";
import { DirectorQuestionsStore } from "./services/questions/gb/directorQuestionsStore";
import { IdentityQuestionsStore } from "./services/questions/gb/identityQuestionsStore";
import { RecommendedSicCodesStore } from "./services/sicCodes/recommendedSicCodesStore";
import { SicCodesClient } from "./services/sicCodes/sicCodesClient";
import { SignUpPageStore } from "./services/signUpPage/gb/signUpPageStore";
import { CompanyStepStore } from "./services/stepStore/gb/companyStepStore";
import { ParticularsStepStore } from "./services/stepStore/gb/particularsStepStore";
import type { UserClient } from "./services/user/userClient";
import { UserStore } from "./services/user/userStore";

export class ServicesGb extends Services {
  constructor(
    protected readonly _baseServices: BaseServices,
    protected readonly _authenticationHandler: AuthenticationHandler,
    protected readonly _apiClient: ApiClient,
    protected readonly _userClient: UserClient,
    protected readonly _userStore: UserStore,
    protected readonly _profileStore: ProfileStore,
    protected readonly _featureStore: FeatureStore,
    protected readonly _navigate: NavigateFunction,
    private readonly _partnerOffersStore: PartnerOffersStore,
  ) {
    super(
      _baseServices,
      _authenticationHandler,
      _apiClient,
      _userClient,
      _userStore,
      _profileStore,
      _featureStore,
      _navigate,
    );
  }

  get partnerOffersStore(): PartnerOffersStore {
    return this._partnerOffersStore;
  }

  get abTestingService(): ABTestingService {
    return this._baseServices.abTestingService;
  }

  @Memoize()
  get billingItemsStore(): BillingItemsStore {
    return new BillingItemsStore();
  }

  @Memoize()
  get companyAddressStore(): CompanyAddressStore {
    return new CompanyAddressStore(
      this.companyStore,
      this.addressClient,
      this.companyProductsStore,
      this.pricingPlanStore,
    );
  }

  @Memoize()
  get companyStepStore(): CompanyStepStore {
    return new CompanyStepStore(this.companyStore, this.companyAddressStore, this.companyProductsStore);
  }

  @Memoize()
  get chVerificationStepStore(): CHVerificationStepStore {
    return new CHVerificationStepStore(
      this.directorStore,
      this.companyStore,
      this.incorporationStore,
      this.analyticsManager,
      this.featureStore,
      this.consentStore,
    );
  }

  @Memoize()
  get identityStepStore(): IdentityStepStore {
    return new IdentityStepStore(this.directorStore, this.sumsubApplicationStore);
  }

  @Memoize()
  get attachmentStore(): AttachmentStore {
    return new AttachmentStore(new AttachmentClient(this.apiClient));
  }

  @Memoize()
  get initStepStore(): InitStepStore {
    return new InitStepStore(this.companyStore, this.companyProductsStore);
  }

  @Memoize()
  get pricingStepStore(): PricingStepStore {
    return new PricingStepStore(this.pricingPlanStore, this.featureStore);
  }
  @Memoize()
  get companyQuestionsStore(): CompanyQuestionsStore {
    return new CompanyQuestionsStore(this.companyStore, this.pricingPlanStore, this.issuesStore);
  }

  @Memoize()
  get initQuestionStore(): InitQuestionStore {
    return new InitQuestionStore(this.formationExpressStore, this.issuesStore);
  }

  @Memoize()
  get companyBusinessActivitiesStore(): CompanyBusinessActivitiesStore {
    return new CompanyBusinessActivitiesStore(
      this.companyStore,
      new CompanyBusinessActivitiesClient(this.apiClient),
      this.addressClient,
    );
  }

  @Memoize()
  get directorQuestionsStore(): DirectorQuestionsStore {
    return new DirectorQuestionsStore(this.directorStore, this.directorAddressStore, this.issuesStore);
  }

  @Memoize()
  get identityQuestionsStore(): IdentityQuestionsStore {
    return new IdentityQuestionsStore(this.addressVerificationStore, this.sumsubApplicationStore, this.issuesStore);
  }

  @Memoize()
  get chVerificationQuestionStoreV2(): CHVerificationQuestionStoreV2 {
    return new CHVerificationQuestionStoreV2(
      this.directorStore,
      this.companyStore,
      this.featureStore,
      this.incorporationStore,
      this.analyticsManager,
      this.issuesStore,
      this.consentStore,
    );
  }

  @Memoize()
  get particularsStepStore(): ParticularsStepStore {
    return new ParticularsStepStore(
      this.directorStore,
      this.companyProductsStore,
      this.companyStore,
      this.directorPositionsStore,
      this.featureStore,
    );
  }

  @Memoize()
  get pricingPlanStore(): PricingPlanStore {
    return new PricingPlanStore(
      this.companyStore,
      this.companyProductsStore,
      new PricingPlanClient(this.apiClient),
      this.analyticsManager,
    );
  }

  @Memoize()
  // Get the DirectorQuestionsStore
  get particularQuestionsStore(): ParticularQuestionStore {
    return new ParticularQuestionStore(this.pricingPlanStore, this.directorStore, this.issuesStore);
  }

  @Memoize()
  get basketQuestionsStore(): BasketQuestionsStore {
    return new BasketQuestionsStore(this.companyStore, this.issuesStore, this.pricingPlanStore, this.featureStore);
  }

  @Memoize()
  get registerFormStepsStore(): IncorporationRegisterFormStepsStore {
    return new IncorporationRegisterFormStepsStore(
      this.companyQuestionsStore,
      this.directorQuestionsStore,
      this.particularQuestionsStore,
      this.identityQuestionsStore,
      this.chVerificationQuestionStoreV2,
      this.basketQuestionsStore,
      this.analyticsManager,
      this.initQuestionStore,
      this.featureStore,
      this.partnerOffersStore,
    );
  }

  @Memoize()
  get checkoutBasketStore(): CheckoutBasketStore {
    return new CheckoutBasketStore(
      this.companyStore,
      this.companyAddressStore,
      this.companyProductsStore,
      this.formationExpressStore,
      this.pricingPlanStore,
      this.billingControllerStore,
      this.featureStore,
    );
  }

  @Memoize()
  get recommendedSicCodesStore(): RecommendedSicCodesStore {
    return new RecommendedSicCodesStore(this.companyStore, this.companyBusinessActivitiesStore, this.sicCodesClient);
  }

  @Memoize()
  get sicCodesClient(): SicCodesClient {
    return new SicCodesClient(this.apiClient);
  }

  @Memoize()
  get expressCheckoutStore(): ExpressCheckoutStore {
    return new ExpressCheckoutStore(this.setupIntentStore, new BillingClient(this.apiClient), this.analyticsManager);
  }

  createCompanyAddressFormStore = (): CompanyAddressFormStore => new CompanyAddressFormStore(this.companyAddressStore);

  createCompanyNameFormStore = (): CompanyNameFormStore => new CompanyNameFormStore(this.companyStore);

  createCheckoutBasketStore = (): CheckoutBasketStore => this.checkoutBasketStore;

  createDirectorFormStore = (directorId?: string): DirectorFormStore => {
    return new DirectorFormStore(
      this.directorStore,
      this.companyStore,
      this.directorAddressStore,
      this.directorPositionsStore,
      this.featureStore.hasFeature("formationUkYearAddressCheck"),
      directorId,
    );
  };

  createRejectionAddressFormStore = (directorId?: string): RejectionAddressStore => {
    return new RejectionAddressStore(
      this.directorStore,
      this.directorAddressStore,
      this.fileShareClient,
      this.attachmentStore,
      this.analyticsManager,
      this.incorporationStore,
      this.registerFormStepsStore,
      this.issuesStore,
      directorId,
    );
  };

  createPricingPlanFormStore = (): PricingPlanFormStore => {
    return new PricingPlanFormStore(this.pricingPlanStore);
  };

  createIncorporationRegisterFormStore = (): IncorporationRegisterFormStore => {
    return new IncorporationRegisterFormStore(
      this.consentStore,
      this.directorStore,
      this.companyStore,
      this.companyAddressStore,
      this.businessActivitiesStore,
      this.companyBusinessActivitiesStore,
      this.companyProductsStore,
      this.particularsStepStore,
      this.paymentMethodsStore,
      this.sumsubApplicationStore,
      this.formationExpressStore,
      this.pricingPlanStore,
      this.userStore,
      this.analyticsManager,
      this.registerFormStepsStore,
      this.incorporationStore,
      this.issuesStore,
      this.companyStepStore,
      this.chVerificationStepStore,
      this.identityStepStore,
      this.initStepStore,
      this.pricingStepStore,
      this.checkoutBasketStore,
      this.featureStore,
      this.billingControllerStore,
      this.identityQuestionsStore,
      this.directorPoaStore,
      this.partnerOffersStore,
    );
  };

  createSignUpPageStore = (): SignUpPageStore =>
    new SignUpPageStore(
      this.companyStore,
      this.companyClient,
      this.leadClient,
      this.analyticsManager,
      this.userStore,
      this._navigate,
      this.config,
      this.companyProductsStore,
      this.abTestingService,
    );

  createDirectorPoaWidgetStore = (): DirectorPoaWidgetStore =>
    new DirectorPoaWidgetStore(
      this.sumsubClient,
      this.addressVerificationStore,
      this.identityQuestionsStore,
      this.featureStore,
      this.analyticsManager,
    );

  createBusinessActivitiesSelectStore = (): BusinessActivitiesSelectStore =>
    new BusinessActivitiesSelectStore(
      this.businessActivitiesClient,
      this.businessActivitiesStore,
      this.companyBusinessActivitiesStore,
    );

  createDirectorCodeModalStore = (directorId?: string): DirectorCodeModalStore => {
    return new DirectorCodeModalStore(
      this.directorStore,
      this.directorPositionsStore,
      this.analyticsManager,
      this.featureStore,
      this.complianceClient,
      directorId,
    );
  };
}
